import React, { useEffect } from 'react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

/**
 * Global Error Handler
 * Sets up axios interceptors for consistent error handling across the app
 */
const GlobalErrorHandler = ({ children }) => {
  useEffect(() => {
    // Response interceptor for API calls
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Don't show toast for certain endpoints or error types
        // These endpoints handle their own error display in their respective components
        const silentEndpoints = [
          '/api/auth/me', 
          '/api/auth/magic-link',
          '/api/auth/login',
          '/api/auth/signup',
          '/api/evaluate-paper',
          '/api/evaluate-paper-progressive',
          '/api/evaluate-paper-consensus',
          '/api/evaluate-paper-parallel',
          '/api/evaluations/save',
          '/api/evaluations/',
          '/api/upload-document',
          '/api/upload-document-fast',
          '/api/industry-benchmark',
          '/api/sessions',
          '/api/usage'
        ];
        const requestUrl = error.config?.url || '';
        const isSilent = silentEndpoints.some(endpoint => requestUrl.includes(endpoint));
        
        // Also silence 401 errors (handled by auth flow)
        const status = error.response?.status;
        const isAuthError = status === 401;

        if (!isSilent && !isAuthError) {
          let errorMessage = 'Something went wrong. Please try again.';
          let errorTitle = 'Error';

          if (error.response) {
            // Server responded with error
            const status = error.response.status;
            const data = error.response.data;

            switch (status) {
              case 400:
                errorTitle = 'Invalid Request';
                errorMessage = typeof data?.detail === 'string' 
                  ? data.detail 
                  : Array.isArray(data?.detail) 
                    ? 'Something went wrong processing your request. Please try again.' 
                    : 'The request could not be processed. Please try again.';
                break;
              case 401:
                errorTitle = 'Authentication Required';
                errorMessage = 'Please log in to continue.';
                // Don't show toast for auth errors on protected routes
                if (!requestUrl.includes('/auth/')) {
                  // Could trigger redirect to login here
                }
                break;
              case 403:
                errorTitle = 'Access Denied';
                errorMessage = 'You don\'t have permission to perform this action.';
                break;
              case 404:
                errorTitle = 'Not Found';
                errorMessage = data?.detail || 'The requested resource was not found.';
                break;
              case 429:
                errorTitle = 'Too Many Requests';
                errorMessage = 'Please wait a moment before trying again.';
                break;
              case 422:
                // Pydantic validation error — suppress technical details
                return Promise.reject(error);
              case 500:
                errorTitle = 'Server Error';
                errorMessage = 'Our servers are having issues. Please try again later.';
                break;
              default:
                errorMessage = data?.detail || data?.message || errorMessage;
            }
          } else if (error.request) {
            // Request made but no response
            errorTitle = 'Network Error';
            errorMessage = 'Unable to connect to the server. Please check your internet connection.';
          }

          // Show toast notification
          toast({
            title: errorTitle,
            description: errorMessage,
            variant: 'destructive',
          });
        }

        return Promise.reject(error);
      }
    );

    // Request interceptor to add auth token
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = localStorage.getItem('if_auth_token');
        if (token && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  return <>{children}</>;
};

export default GlobalErrorHandler;
